import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MessageDescriptor } from "react-intl";

import api from "@api";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Divider } from "@UI/Components/Divider/Divider";
import { Flex } from "@UI/Components/Flex/Flex";
import { FormControl, FormErrorMessage } from "@UI/Components/Form/Form";
import { Heading } from "@UI/Components/Heading/Heading";
import { Icon } from "@UI/Components/Icon/Icon";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { Input } from "@UI/Components/Input/Input";
import { Text } from "@UI/Components/Text/Text";
import { VStack } from "@UI/Components/Stack/Stack";
import { useToast } from "@UI/hooks/useToast";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import useForm from "@Libs/Utils/hooks/useForm";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { usePhotoAttachments } from "@Shared/hooks/usePhotoAttachments";

import { serviceRequestPathFragment } from "../../../config/routes";
import { useDescriptionDirty } from "../../Contexts/DescriptionDirty";
import { TASK_CREATION_HEADER_HEIGHT } from "../../Components/TaskCreationHeader/TaskCreationHeader";
import {
  DescriptionField,
  IDescriptionPhoto,
} from "../../Components/DescriptionField/DescriptionField";
import { AiLoaderModal } from "../../Components/AiLoaderModal/AiLoaderModal";
import { ContactInfoModal } from "../../Components/ContactInfoModal/ContactInfoModal";
import { TaskCreationResumeModal } from "../../Components/TaskCreationResumeModal/TaskCreationResumeModal";
import { ITaskCreationSummaryStep } from "../../Components/TaskCreationSummary/TaskCreationSummary";
import {
  ALLOWED_P_CATEGORY_IDS,
  DESCRIPTION_LIMITS,
  TASK_CREATION_FULL_FLAG,
  DESCRIPTION_PHOTO_LIMITS,
  SUBCATEGORY_SCREEN_KEY,
  TASK_PHOTO_CATEGORY,
} from "../../config/constants";
import {
  buildTrackingParams,
  DESCRIPTION_STEP_NAME,
  TRACKING_EVENTS,
  trackFunnelEvent,
  trackNoCategoryFit,
} from "../../config/tracking";
// Category-name lookup, built in parallel (RT-4321).
import { getCategoryMessage } from "../../utils/messageKeys";
import { useCreateServiceRequestDraft } from "../../hooks/useCreateServiceRequestDraft";
import { useDraftIdentity } from "../../hooks/useDraftIdentity";
import { getDraftToken, getLastScreen } from "../../hooks/useDraftStorage";
import { useDescriptionBack } from "../../hooks/useFlowBack";
import { useMinDuration } from "../../hooks/useMinDuration";
import { useOngoingDraftGuard } from "../../hooks/useOngoingDraftGuard";
import { usePhotoSelection } from "../../hooks/usePhotoSelection";
import { usePhotoToast } from "../../hooks/usePhotoToast";
import {
  useSaveScreen,
  useSeedDraftPhotos,
  useServiceRequestDraft,
  useTaskCreationConfig,
} from "../../hooks/useServiceRequestDraft";
import {
  appendDictation,
  useSpeechDictation,
} from "../../hooks/useSpeechDictation";
import detailMessages from "../../../Components/messages";
import messages from "../../messages";
import { buildFlowShape, toAnswerMap } from "../../utils/buildFlowShape";
import { getPlaceholderMessages } from "../../utils/descriptionPlaceholders";
import {
  CONTACT_INFO_ERROR,
  hasContactInfoError,
  noContactInfo,
} from "../../utils/contactInfo";
import {
  isBackForwardNavigation,
  markDescriptionAsFlowEntry,
} from "../../utils/isBackForwardNavigation";
import { getServiceProviderPins } from "../../utils/serviceProviderPins";
import { ServiceProviderMap } from "../../Components/ServiceProviderMap/ServiceProviderMap";

/**
 * The flow's entry screen: one description, one CTA, one POST (RT-4321 M2).
 *
 * Mounted by `ServiceRequestCreationRoot` at `service_request/new` and
 * `service_request/new/description`.
 */

/**
 * Interim, per Sam: the Rails category picker reads this to explain why the
 * request left the flow. One constant so renaming it is one line.
 */
const NOT_SUPPORTED_PARAM = "not_supported";

/**
 * Query params the legacy category picker reads to render its two AI-mismatch
 * banners (RT-4383) — `app/views/tasks/task_creation/_step_1.html.slim`.
 */
const SUGGESTED_CATEGORY_PARAM = "suggested_category";
const NO_FITTING_CATEGORY_PARAM = "error";

/**
 * The resume modal's step rows, of which this screen has none: the flow reports
 * no screens outside the steps (`deriveFlowState` returns the empty state
 * without a `screenKey`), so there is no status to show and the modal is its
 * question plus the two CTAs. Hoisted so the memoised modal sees one array.
 */
const NO_SUMMARY_STEPS: ITaskCreationSummaryStep[] = [];

/** Floors the loader's visible time so a fast draft save doesn't flash it. */
const MIN_LOADER_DURATION_MS = 5000;

/**
 * Rails wraps every draft-POST rejection as `{ error: <code> }`
 * (`API::Rescuable#render_error!`), not `{ code: <code> }`. `NOT_BRICOLAGE`,
 * `CATEGORY_MISMATCH` and `NO_FITTING_CATEGORY` aren't live on
 * `ServiceRequestDraftsController` yet — RT-4383's backend is still in review.
 */
const ERROR_CODES = {
  TOO_SHORT: "description_too_short",
  TOO_LONG: "description_too_long",
  MISSING: "description_missing",
  INVALID: "invalid_description",
  NOT_BRICOLAGE: "category_not_supported",
  CATEGORY_MISMATCH: "category_mismatch",
  NO_FITTING_CATEGORY: "no_fitting_category",
} as const;

/** Fixed ids so a repeated failure replaces its toast instead of stacking. */
const REJECTED_TOAST_ID = "taskCreation.description.rejected";
const SAVE_FAILED_TOAST_ID = "taskCreation.description.saveFailed";
/**
 * Per code, not one shared id: `not-allowed` is sticky in Chrome until the user
 * changes a browser setting, so it must not silence a later `network` toast.
 */
const DICTATION_TOAST_ID = "taskCreation.dictation.error";

/**
 * The Web Speech codes worth a word (RT-4340). `aborted` and `no-speech` never
 * reach here — the hook swallows them, since both fire in normal use. Anything
 * else, including a code this table predates, takes the generic message.
 *
 * `service-not-allowed` and `language-not-supported` also hide the mic for the
 * session; that part lives in the hook, which flips `supported` itself.
 */
/**
 * Only the failure the user can act on gets its own wording — a blocked
 * permission is fixable, in browser settings. Every other cause (no mic, lost
 * connection, unsupported language, service refused) reads the same to the
 * user, so they fall through to the generic message below.
 */
const DICTATION_ERROR_MESSAGES: Record<string, MessageDescriptor> = {
  "not-allowed": messages["taskCreation.dictation.error.notAllowed"],
};

interface IDraftErrorData {
  error?: string;
  suggested_p_category_id?: number;
}

const getErrorData = (error: unknown): IDraftErrorData | undefined => {
  if (!api.isApiError(error)) return undefined;

  return error.response?.data;
};

const getErrorCode = (error: unknown): string | undefined =>
  getErrorData(error)?.error;

interface IDescriptionFormValues {
  description: string;
  title: string;
}

export const DescriptionScreen = () => {
  const { formatMessage } = useI18n();
  const navigate = useNavigate();
  // For the three hops out to the legacy picker below, as a real browser
  // load — see `leaveToLegacyPicker`.
  const { generateRoute } = useRoutes();
  const toast = useToast();

  // Editing an existing request: Rails serialized it into the page, and this
  // screen updates that request instead of minting a draft.
  const identity = useDraftIdentity();
  const { isEditing, screenRoute } = identity;

  const pCategory = useSearchParams("p_category");
  // Same param name the Rails side already uses for the pair
  // (`app/controllers/tasks_controller.rb:334`). Only the SEM pages send it;
  // the category picker sends `p_category` alone.
  const sCategory = useSearchParams("s_category");
  // Set only by `useFlowLeaveGuard.requestLeave` — the subcategory screen's
  // Back button. Distinguishes "stepping back into a draft that's already
  // mid-flow" from every other way this screen is reached (fresh visit, stale
  // bookmark, URL edit), which all still get the resume/discard modal below.
  //
  // Gated on an actual draft token too: a spoofed/stale `from_step=1` with
  // nothing in storage would otherwise PUT against an empty public token
  // instead of falling back to the normal create-a-draft path.
  //
  // `isBackForwardNavigation()` is the same statement for the one back press
  // this widget cannot put a param on: a browser back that crosses into the
  // document the flow was entered from restores that URL exactly as it was
  // first loaded. The *navigation type* is what separates that from arriving
  // here fresh — a link, a bookmark, a second visit — which still gets the
  // modal even with a draft in storage (RT-4321).
  const fromStep =
    !isEditing &&
    (useSearchParams("from_step") === "1" || isBackForwardNavigation()) &&
    Boolean(getDraftToken());

  // Both arrive as strings and feed numeric ids, so a junk value has to read
  // as absent — `Number("abc")` is NaN, which serialises to null and would reach
  // the backend as a missing category rather than a bad request.
  const urlPCategoryId = pCategory ? Number(pCategory) : undefined;

  const sCategoryId = Number(sCategory);
  const hasSCategory = Boolean(sCategory) && Number.isFinite(sCategoryId);

  // Not `useTaskCreationFlow()` — that context's `draft` is only ever
  // populated when a `screenKey` is in the URL (`deriveFlowState`), which this
  // route never has, so it reports no draft here even once one exists.
  // Fetched directly against the same token instead.
  const { data: draft, isLoading: isDraftLoading } = useServiceRequestDraft(
    identity,
    { enabled: fromStep }
  );

  // The backend draft is authoritative once it exists. The URL is the fallback
  // for a fresh entry, before there is a draft to answer from.
  //
  // This gate used to read the URL alone, which made every hop that dropped
  // `p_category` an exit from the flow: `requestLeave` sends a back press here,
  // and a bare URL tripped the redirect below into `reload`ing out to the Rails
  // picker — one back press, two hops, and the visitor lands on
  // `/task_creation` having asked only to step back one screen (RT-4321).
  const pCategoryId = draft?.p_category_id ?? urlPCategoryId;
  // Whitelisted, not merely present: a theme this flow does not support is as
  // unusable as no theme at all, and both go back to the picker below.
  const hasCategory =
    pCategoryId !== undefined && ALLOWED_P_CATEGORY_IDS.includes(pCategoryId);

  // Which subcategory this screen is *about*, for the copy and the map — the
  // draft first, the URL only as the entry's opening bid.
  //
  // The draft is the one source that survives a hop: `s_category` rides the
  // description and picker URLs alone (see `useFlowCategoryParams`), so a
  // visitor stepping back onto this screen arrives without it and used to be
  // told about "service providers" after a whole flow spent as a painter's
  // customer. The draft has carried `s_category_id` since the picker — or
  // since this screen's own first submit, for a SEM entry (RT-4321).
  //
  // Deliberately *not* reused for the two decisions below that the URL owns:
  // which subcategory to PUT (a repeat `s_category_id` resets the draft's
  // answers server-side — `UpdateDraft#s_category_side_effects`), and whether
  // a resubmit may skip the picker.
  const categorySCategoryId = hasSCategory ? sCategoryId : draft?.s_category_id;

  // Read for tracking only — this screen renders the same either way. Taken
  // from `useCurrentUser` rather than `useTaskCreationFlow`, because this is the
  // flow's entry screen and must not require the provider to mount.
  const { isSuccess: isLoggedIn } = useCurrentUser();

  // The category *tree* (`useCategoryKey`) is the wrong source here: its
  // `keyName` is the s_category's raw DB slug (e.g. "peintre"), locale-
  // dependent and never declared in `messages.ts`. The per-category config
  // endpoint instead returns `category.key`, the flow's own semantic
  // identifier ("painter") that the fixtures and `taskCreation.category.*`
  // messages actually use — and it's public, so it works logged out too.
  const {
    data: subcategoryConfig,
    isLoading: isCategoryLoading,
    error: categoryError,
  } = useTaskCreationConfig(categorySCategoryId);

  // The heading names the s_category and nothing else: "top plumbers" is the
  // point, and the theme ("DIY pros") is not a phrase anyone searched for. So
  // the p_category is deliberately not resolved for the heading, even though
  // the tree can do that — every case without an s_category gets the generic
  // sentence, which carries its own bold half.
  //
  // Fall back while loading, on a fetch error, or when the category has no
  // configured message. No spinner — the fallback→specific swap is the only
  // visible change.
  const categoryMessage =
    !isCategoryLoading && !categoryError && subcategoryConfig
      ? getCategoryMessage(subcategoryConfig.category.key)
      : undefined;
  const category = categoryMessage ? formatMessage(categoryMessage) : undefined;
  const headingMessage = category
    ? messages["taskCreation.description.heading"]
    : messages["taskCreation.description.heading.fallback"];

  const { mutate, isLoading } = useCreateServiceRequestDraft();
  const saveScreen = useSaveScreen(identity);

  // A draft already on this device is resumed or thrown away *before* the form
  // can mint a second one (RT-4321) — except when `fromStep` is set: that
  // draft is the one this very screen is stepping back into, so the modal
  // stays closed (see `TaskCreationResumeModal isOpen` below).
  const ongoingDraft = useOngoingDraftGuard();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    reset,
    formState: { errors },
  } = useForm<IDescriptionFormValues>({
    defaultValues: { description: "", title: "" },
  });

  const isModerationEnabled = useFeatureFlag(TASK_CREATION_FULL_FLAG);

  const [isContactInfoModalOpen, setContactInfoModalOpen] = useState(false);
  const closeContactInfoModal = useCallback(
    () => setContactInfoModalOpen(false),
    []
  );

  // Contact info is answered with a modal, so the validator renders no message
  // and the rejected submit has to be caught here.
  const onInvalid = (invalidFields: typeof errors) => {
    if (hasContactInfoError(invalidFields)) setContactInfoModalOpen(true);
  };

  const {
    supported: isDictationSupported,
    listening: isListening,
    interim,
    error: dictationError,
    stop: stopDictation,
    toggle: toggleDictation,
  } = useSpeechDictation({
    // `getValues`, not the watched value: a finalized segment can land between
    // renders, and reading the form's own store means two segments in quick
    // succession never overwrite one another.
    //
    // No length guard — dictation appends exactly like typing does, so passing
    // the limit turns the counter red and the CTA's validation catches it.
    onSegment: (segment) =>
      setValue(
        "description",
        appendDictation(getValues("description"), segment),
        { shouldDirty: true }
      ),
  });

  // Bricolage-only flow, gated by the category picker and the SEM pages
  // (decisions 14 and 17). A missing or unsupported `p_category` means the
  // visitor came from outside the gate, so send them to the picker rather than
  // render a form for a theme this flow cannot handle.
  //
  // Held while the draft is still loading: on a step back the theme is *in*
  // that response, so ejecting before it lands would throw the visitor out on
  // every one of these back presses.
  useEffect(() => {
    if (hasCategory || isDraftLoading) return;

    navigate("REQUEST_SERVICE", {
      withLocale: true,
      reload: true,
      replace: true,
    });
  }, [hasCategory, isDraftLoading]);

  // The category picker is where the user came from — no other "back"
  // destination exists yet for this entry screen.
  const handleBack = useDescriptionBack();

  const description = watch("description");
  const displayedDescription = appendDictation(description, interim);

  // Same guard as `categoryMessage` above: only a settled config names a trade.
  const placeholderCategoryKey =
    !isCategoryLoading && !categoryError && subcategoryConfig
      ? subcategoryConfig.category.key
      : undefined;
  const placeholders = useMemo(
    () =>
      getPlaceholderMessages(placeholderCategoryKey).map((message) =>
        formatMessage(message)
      ),
    [formatMessage, placeholderCategoryKey]
  );

  const showToast = (id: string, message: MessageDescriptor) => {
    if (toast.isActive(id)) return;

    toast({ id, status: "error", description: formatMessage(message) });
  };

  // The hook clears `error` on every start, so a repeat of the same failure
  // re-fires this; `showToast` then dedupes it against the live toast.
  useEffect(() => {
    if (!dictationError) return;

    showToast(
      `${DICTATION_TOAST_ID}.${dictationError}`,
      DICTATION_ERROR_MESSAGES[dictationError] ??
        messages["taskCreation.dictation.error.generic"]
    );
  }, [dictationError]);

  /**
   * Photos, uploaded straight from this screen rather than held until submit.
   *
   * A `task_photos` attachment starts life unattached (see
   * `TASK_PHOTO_CATEGORY`), so a visitor can add pictures before any draft
   * exists and the POST below only has to name the ids it got back. `url` is
   * the local `blob:` preview — the server's own thumbnail points at a variant
   * a background job has not generated yet.
   */
  const [photos, setPhotos] = useState<IDescriptionPhoto[]>([]);
  // Latest `photos` for the remove callback, which runs on its own schedule (a
  // DELETE round trip) and must not read the list as it was when the × was
  // clicked — a photo added meanwhile would otherwise lose its preview.
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const seedDraftPhotos = useSeedDraftPhotos();
  const showPhotoToast = usePhotoToast();
  /** Set once a draft exists to carry these previews past this screen. */
  const arePhotosHandedOver = useRef(false);

  // Holds the next-screen navigation once the save succeeds, so it can fire
  // only once `isSavingPadded` (below) clears: `navigate` unmounts this
  // screen — and `AiLoaderModal` with it — immediately, so calling it straight
  // from `onSuccess` would tear the loader down the instant the network call
  // resolves, however long `MIN_LOADER_DURATION_MS` claims to hold it open.
  // State, not a ref: the effect below must re-run whenever *either* this or
  // `isSavingPadded` changes, in whichever order they happen to settle in —
  // a ref update alone would not schedule that re-run.
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);

  // Stepping back from the subcategory screen (RT-4321): the draft already
  // holds this description and these photos, so show them instead of the
  // blank form the resume modal would otherwise gate. Guarded so a draft
  // refetch (or the user clearing the field) can't re-stomp their edits.
  const hasPrefilled = useRef(false);
  useEffect(() => {
    if ((!fromStep && !isEditing) || !draft || hasPrefilled.current) return;

    hasPrefilled.current = true;
    reset({ description: draft.description, title: draft.title ?? "" });
    setPhotos(
      (draft.photo_attachments ?? []).map((photo) => ({
        id: photo.id,
        url: photo.thumb_url,
      }))
    );
  }, [fromStep, isEditing, draft]);

  // A preview is only worth keeping while something can still show it. Once a
  // draft has them they belong to the flow and must outlive this mount (the
  // photos screen renders the very same `blob:` URLs); a visitor who leaves
  // without submitting leaves nothing behind, so those blobs are released
  // rather than pinned for the life of the document.
  useEffect(
    () => () => {
      if (arePhotosHandedOver.current) return;

      photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
    },
    []
  );

  const {
    upload: uploadPhotos,
    remove: removePhoto,
    removingId: removingPhotoId,
    pendingUploads,
  } = usePhotoAttachments({
    category: TASK_PHOTO_CATEGORY,
    // No `photoAttachableId` — there is no draft to attach these to yet.
    remainingSlots: DESCRIPTION_PHOTO_LIMITS.MAX_FILES - photos.length,
    onUploadSuccess: (photo, previewUrl) =>
      setPhotos((current) => [
        ...current,
        { id: Number(photo.id), url: previewUrl },
      ]),
    onUploadError: () =>
      showPhotoToast(
        formatMessage(messages["taskCreation.photos.uploadError"])
      ),
    onRemoveSuccess: (id) => {
      const numericId = Number(id);
      const removed = photosRef.current.find((photo) => photo.id === numericId);

      if (removed) URL.revokeObjectURL(removed.url);
      setPhotos((current) => current.filter((photo) => photo.id !== numericId));
    },
    onRemoveError: () =>
      showPhotoToast(
        formatMessage(messages["taskCreation.photos.removeError"])
      ),
  });

  // The hook truncates to `remainingSlots`, which is this render's count and
  // knows nothing of the uploads it already has in flight — the same
  // adjustment `PhotoUploader` makes for the photos screen.
  const availablePhotoSlots =
    DESCRIPTION_PHOTO_LIMITS.MAX_FILES - photos.length - pendingUploads.length;
  // Submitting mid-upload would create the draft without the photo still in
  // flight; mid-removal, with one the server is about to delete.
  const isPhotoBusy = pendingUploads.length > 0 || removingPhotoId != null;

  // Reported up to `useFlowExitGuard` (RT-4321): the "Leaving already?" modal
  // only has something to warn about once the visitor has typed a
  // description or touched the photo picker, one way or another.
  const { setDirty } = useDescriptionDirty();
  useEffect(() => {
    setDirty(
      Boolean(description?.trim()) ||
        photos.length > 0 ||
        pendingUploads.length > 0
    );
  }, [description, photos, pendingUploads, setDirty]);

  const { selectFiles, notifyLimitReached } = usePhotoSelection({
    allowedExtensions: [...DESCRIPTION_PHOTO_LIMITS.ALLOWED_EXTENSIONS],
    maxFiles: DESCRIPTION_PHOTO_LIMITS.MAX_FILES,
    availableSlots: availablePhotoSlots,
  });

  const handlePhotosSelected = (files: FileList | null) => {
    // Whatever `selectFiles` drops, it has already explained — an empty result
    // means the user has been told why nothing is happening.
    const accepted = selectFiles(files);

    if (accepted.length > 0) uploadPhotos(accepted);
  };

  // A plain document load, not `navigate(..., { reload: true })`: that
  // pushes the new URL through react-router and only *then* reloads, so
  // this still-mounted screen re-renders once more while the load is
  // already in flight — with `p_category` gone from that URL, the "no
  // category" guard above fires its own `reload: true` eject, and *that*
  // pushState wins the race, dropping this query param off the URL that
  // actually loads (RT-4383; same failure as `DynamicScreen`'s
  // `handleSeeMyRequest`, RT-4321). `assign` never touches router state, so
  // nothing re-renders and there is only ever one navigation.
  const leaveToLegacyPicker = (queryParams: Record<string, string>) => {
    window.location.assign(
      generateRoute(
        { fragments: "REQUEST_SERVICE", options: { withLocale: true } },
        undefined,
        queryParams
      )
    );
  };

  const handleError = (error: unknown) => {
    switch (getErrorCode(error)) {
      case ERROR_CODES.TOO_SHORT:
        setError("description", {
          message: formatMessage(
            messages["taskCreation.validation.minLength"],
            {
              min: DESCRIPTION_LIMITS.MIN_LENGTH,
            }
          ),
        });
        return;
      case ERROR_CODES.TOO_LONG:
        setError("description", {
          message: formatMessage(
            messages["taskCreation.validation.maxLength"],
            {
              max: DESCRIPTION_LIMITS.MAX_LENGTH,
            }
          ),
        });
        return;
      case ERROR_CODES.MISSING:
        setError("description", {
          message: formatMessage(messages["taskCreation.validation.required"]),
        });
        return;
      case ERROR_CODES.INVALID:
        // Decision 2: stay on the screen, the typed text is untouched.
        showToast(
          REJECTED_TOAST_ID,
          messages["taskCreation.error.descriptionRejected"]
        );
        return;
      case ERROR_CODES.NOT_BRICOLAGE:
        leaveToLegacyPicker({ [NOT_SUPPORTED_PARAM]: "1" });
        return;
      case ERROR_CODES.CATEGORY_MISMATCH:
        leaveToLegacyPicker({
          [SUGGESTED_CATEGORY_PARAM]: String(
            getErrorData(error)?.suggested_p_category_id
          ),
        });
        return;
      case ERROR_CODES.NO_FITTING_CATEGORY:
        trackNoCategoryFit({
          description: getValues("description").trim(),
          draft,
          screenKey: DESCRIPTION_STEP_NAME,
          isLoggedIn,
          isEditing,
          pCategoryId,
          sCategoryId: hasSCategory ? sCategoryId : undefined,
        });
        leaveToLegacyPicker({
          [NO_FITTING_CATEGORY_PARAM]: "no_category_match",
        });
        return;
      default:
        showToast(
          SAVE_FAILED_TOAST_ID,
          messages["taskCreation.error.saveFailed"]
        );
    }
  };

  // Where editing goes next: the edit flow has no subcategory picker
  // (`buildFlowShape`), so the first screen is whichever the config puts first.
  const firstEditScreenKey =
    isEditing && subcategoryConfig
      ? buildFlowShape(subcategoryConfig, toAnswerMap(draft?.answers), {
          isLoggedIn,
          mode: "edit",
        }).screens[0]?.key
      : undefined;

  const onSubmit = ({ description, title }: IDescriptionFormValues) => {
    if (pCategoryId === undefined) return;

    // Editing saves the request in place and moves on — no new draft, no
    // subcategory to pick, and no assistant to wait for.
    if (isEditing) {
      if (!firstEditScreenKey) return;

      saveScreen.mutate(
        {
          title: title.trim(),
          description: description.trim(),
          ...(photos.length > 0
            ? { photo_attachments: photos.map((photo) => photo.id) }
            : {}),
        },
        {
          onSuccess: () =>
            setPendingNavigation(
              () => () =>
                navigate(screenRoute(firstEditScreenKey).fragments, {
                  params: screenRoute(firstEditScreenKey).params,
                })
            ),
          onError: handleError,
        }
      );
      return;
    }

    // Stepping back into an in-progress draft (RT-4321): update it in place
    // rather than minting a second one via the POST below.
    if (fromStep) {
      saveScreen.mutate(
        {
          description: description.trim(),
          ...(hasSCategory ? { s_category_id: sCategoryId } : {}),
          ...(photos.length > 0
            ? { photo_attachments: photos.map((photo) => photo.id) }
            : {}),
        },
        {
          onSuccess: () => {
            markDescriptionAsFlowEntry();
            setPendingNavigation(
              () => () =>
                navigate(
                  serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
                  {
                    withLocale: true,
                    // The picker, not `getLastScreen()`: stepping back here and
                    // submitting again used to leap straight to wherever the
                    // visitor had got to, skipping the subcategory step — and a
                    // rewritten description can well imply a different
                    // subcategory than the one they had picked. A real SEM
                    // visitor still skips it, below, having already named theirs.
                    params: {
                      screenKey: hasSCategory
                        ? getLastScreen() || SUBCATEGORY_SCREEN_KEY
                        : SUBCATEGORY_SCREEN_KEY,
                    },
                    queryParams: {
                      ...(pCategoryId === undefined
                        ? {}
                        : { p_category: String(pCategoryId) }),
                      ...(hasSCategory
                        ? { s_category: String(sCategoryId) }
                        : {}),
                    },
                  }
                )
            );
          },
          onError: handleError,
        }
      );
      return;
    }

    mutate(
      {
        // Trimmed, so the string that passed validation is the string the
        // backend receives — otherwise trailing whitespace could push a
        // just-valid description past the limit it was checked against.
        description: description.trim(),
        p_category_id: pCategoryId,
        // A SEM visitor already told us their s_category — sending it here
        // means the draft is created with it set, so the subcategory screen
        // has something to skip *to* (its own config) instead of skipping to
        // a config that cannot load until a subcategory exists (RT-4321).
        ...(hasSCategory ? { s_category_id: sCategoryId } : {}),
        // Ids only: the files are already on the server, and this is what
        // links them to the draft the POST is about to create.
        ...(photos.length > 0
          ? { photo_attachments: photos.map((photo) => photo.id) }
          : {}),
      },
      {
        onSuccess: ({ service_request }) => {
          // Fired on the POST's success, not on the click: HUB-5279 counts a
          // description that reached the draft, and a failed create leaves the
          // user on this screen with nothing recorded.
          trackFunnelEvent(
            TRACKING_EVENTS.DESCRIPTION_SUBMITTED,
            buildTrackingParams({
              draft: service_request,
              screenKey: DESCRIPTION_STEP_NAME,
              isLoggedIn,
              // A main-page entry has no subcategory yet, so the draft has no theme.
              pCategoryId,
            })
          );

          // The response lists these back with the server's own thumbnail
          // URLs, which 403 until a background job has processed them — so the
          // photos screen is handed this screen's local previews instead.
          arePhotosHandedOver.current = true;
          seedDraftPhotos(
            service_request.signed_id,
            photos.map((photo) => ({
              id: photo.id,
              thumb_url: photo.url,
              normal_url: photo.url,
              description: "",
            }))
          );

          markDescriptionAsFlowEntry();
          setPendingNavigation(
            () => () =>
              navigate(
                serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN,
                {
                  withLocale: true,
                  params: { screenKey: SUBCATEGORY_SCREEN_KEY },
                  // `navigate` builds a path from `params` alone, so a param not
                  // forwarded here dies here. `s_category` lets the flow skip
                  // the subcategory screen for an SEM visitor, who already told
                  // us their s_category. This is a push, not a replace:
                  // description must remain the real history entry before the
                  // flow so browser Back reaches it before `/task_creation`.
                  queryParams: {
                    ...(pCategoryId === undefined
                      ? {}
                      : { p_category: String(pCategoryId) }),
                    ...(hasSCategory
                      ? { s_category: String(sCategoryId) }
                      : {}),
                  },
                }
              )
          );
        },
        onError: handleError,
      }
    );
  };

  // Title is only shown, and so only validated, in edit mode.
  const titleField = isEditing
    ? register("title", {
        validate: (value: string) =>
          value.trim().length > 0 ||
          formatMessage(messages["js_validate.required"]),
      })
    : undefined;

  const {
    ref,
    onChange: onDescriptionChange,
    ...descriptionField
  } = register("description", {
    // Fires on CTA click only — react-hook-form's default submit mode. The
    // error then clears as the user types (default `reValidateMode`).
    validate: {
      length: (value: string) => {
        // Trimmed, or twenty spaces would clear the minimum. The counter
        // still counts what is actually in the box.
        const { length } = value.trim();

        if (length === 0)
          return formatMessage(messages["taskCreation.validation.required"]);

        if (length < DESCRIPTION_LIMITS.MIN_LENGTH)
          return formatMessage(messages["taskCreation.validation.minLength"], {
            min: DESCRIPTION_LIMITS.MIN_LENGTH,
          });

        if (length > DESCRIPTION_LIMITS.MAX_LENGTH)
          return formatMessage(messages["taskCreation.validation.maxLength"], {
            max: DESCRIPTION_LIMITS.MAX_LENGTH,
          });

        return true;
      },
      [CONTACT_INFO_ERROR]: (value: string) =>
        !isModerationEnabled || noContactInfo(value),
    },
  });

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    // A manual edit confirms the text currently shown in the field, including
    // any interim words. Stopping first persists interim speech; this value then
    // replaces it with the client's edit.
    if (interim) {
      stopDictation();
      setValue("description", event.target.value, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    onDescriptionChange(event);
  };

  // The PUT's own loading state when stepping back into a draft; the POST's
  // otherwise. Kept local rather than renaming either mutation's `isLoading`.
  const isSaving = fromStep || isEditing ? saveScreen.isLoading : isLoading;
  // Floors the loader's visible time so a fast response doesn't flash it.
  // Editing skips both the loader and its floor: nothing is being classified,
  // so there is nothing to wait for.
  const isSavingPadded = useMinDuration(
    isSaving && !isEditing,
    MIN_LOADER_DURATION_MS
  );

  // Fires the navigation `onSuccess` queued above once the floor has passed —
  // not sooner, or the loader would still be torn down by the unmount before
  // its minimum has elapsed. Keyed on both: `onSuccess` and the padding
  // clearing can settle in either order, and only a re-run triggered by
  // whichever lands second is guaranteed to see both conditions true at once.
  useEffect(() => {
    if (isSavingPadded || !pendingNavigation) return;

    pendingNavigation();
    setPendingNavigation(null);
  }, [isSavingPadded, pendingNavigation]);

  if (!hasCategory && !isDraftLoading) return null;

  // Static until the backend exposes per-category prices and nearby
  // service-provider first names (RT-4506).
  const serviceProviderPins = getServiceProviderPins(
    categorySCategoryId,
    formatMessage
  );

  return (
    <>
      <TaskCreationResumeModal
        // Suppressed when stepping back from the subcategory screen: that
        // draft belongs to the visitor already mid-flow, not one to resume or
        // discard (RT-4321).
        // Nor when editing: that draft belongs to an unrelated create flow and
        // this screen is not about to mint a second one.
        isOpen={ongoingDraft.isOpen && !fromStep && !isEditing}
        // Closing it resumes: there is nothing to decide by dismissing, and
        // leaving it closed over an untouched draft is the orphaning this
        // guard exists to prevent.
        onClose={ongoingDraft.onContinue}
        onContinue={ongoingDraft.onContinue}
        onDiscard={ongoingDraft.onDiscard}
        steps={NO_SUMMARY_STEPS}
      />
      <AiLoaderModal isOpen={isSavingPadded} />
      <ContactInfoModal
        isOpen={isContactInfoModalOpen}
        onClose={closeContactInfoModal}
      />
      {/* No header here: `TaskCreationShell` mounts the shared one at the base
          route. This screen has no step, so it gets the logo alone — no step
          indicator, no progress bar, no summary. */}
      {/* Minus the header: this box is the header's sibling, so a bare 100vh
          here is 100vh *below* it and scrolls the page by the header's height
          no matter how short the content is. */}
      <Box
        background="sand.100"
        minHeight={`calc(100vh - ${TASK_CREATION_HEADER_HEIGHT})`}
      >
        <Box
          maxWidth="1292px"
          paddingX="0"
          paddingY={{ base: "0", lg: "32px" }}
          paddingBottom={{ lg: "80px" }}
          // 4K screens. The theme has no breakpoint this wide (its largest,
          // `2xl`, is 1536px), so the media query is written out here.
          sx={{
            "@media (min-width: 2560px)": {
              maxWidth: "1800px",
              paddingLeft: "10%",
            },
          }}
        >
          {/* Flush against the row below (Figma has no side gutter here) — the
              back link is the only thing inset, by its own 32px. */}
          <Box
            display={{ base: "none", lg: "block" }}
            paddingLeft={{ lg: "32px" }}
            marginBottom="32px"
          >
            <BackLink onClick={handleBack} />
          </Box>
          <Flex direction={{ base: "column", lg: "row" }} alignItems="stretch">
            {/* Decorative only — service providers near you. Two crops rather
                  than one scaled image: the desktop art is a tall column left of
                  the form, the mobile art a wide strip above it. Hidden from
                  assistive tech, it carries no information the copy does not
                  already give. Kept first in the DOM (left column on desktop,
                  above the form on mobile) per product's explicit call. */}
            <Box
              flex="0 0 509px"
              height="542px"
              display={{ base: "none", lg: "block" }}
              aria-hidden
            >
              <ServiceProviderMap
                variant="desktop"
                side="left"
                pins={serviceProviderPins}
              />
            </Box>
            {!isEditing && (
              <Box display={{ base: "block", lg: "none" }} aria-hidden>
                <ServiceProviderMap
                  variant="mobile"
                  pins={serviceProviderPins.slice(0, 3)}
                />
              </Box>
            )}
            <Flex
              direction="column"
              flex="1"
              // Figma's 133px gutter lives on this container as left padding,
              // not as a gap between the two columns. One padding declaration
              // per axis so a later `paddingX` cannot silently override it.
              paddingLeft={{ base: "16px", lg: "133px" }}
              paddingRight={{ base: "16px", xl: "0" }}
              paddingTop={isEditing ? { base: "24px", lg: "0" } : undefined}
              minHeight={{ lg: "601px" }}
            >
              <form
                onSubmit={handleSubmit(onSubmit, onInvalid)}
                noValidate
                style={{ display: "flex", flexDirection: "column", flex: 1 }}
              >
                <VStack alignItems="stretch" spacing="16px">
                  {isEditing && (
                    <>
                      <Heading
                        as="h2"
                        fontFamily="heading"
                        fontWeight="bold"
                        fontSize={{ base: "3xl", lg: "5xl" }}
                        lineHeight="1"
                        color="gray.800"
                      >
                        <FormattedMessage
                          {...messages["taskCreation.title.heading"]}
                        />
                      </Heading>
                      <FormControl isInvalid={!!errors.title}>
                        <Input
                          {...titleField}
                          height="auto"
                          px="12px"
                          py="16px"
                          bg="white"
                          borderColor="gray.400"
                          borderRadius="10px"
                          // Figma keeps the plain border on error; only the message turns red.
                          _invalid={{
                            borderColor: "gray.400",
                            boxShadow: "none",
                          }}
                          fontFamily="heading"
                          fontSize={{ base: "xl", lg: "3xl" }}
                          fontWeight="bold"
                          lineHeight="1"
                          color="gray.800"
                          isReadOnly={isSaving}
                          aria-label={formatMessage(
                            messages["taskCreation.title.heading"]
                          )}
                        />
                        {errors.title && (
                          <FormErrorMessage>
                            {errors.title.message}
                          </FormErrorMessage>
                        )}
                      </FormControl>
                      <Text fontSize="xs" lineHeight="1.5" color="gray.700">
                        <FormattedMessage
                          {...messages["taskCreation.title.subheading"]}
                        />
                      </Text>
                      <Divider borderColor="gray.200" />
                    </>
                  )}
                  <Heading
                    as="h1"
                    fontFamily={isEditing ? "heading" : "headingLight"}
                    fontWeight={isEditing ? "bold" : "light"}
                    fontSize={
                      isEditing
                        ? { base: "3xl", lg: "5xl" }
                        : { base: "2.25rem", lg: "3.75rem" }
                    }
                    lineHeight="1"
                    color="gray.800"
                  >
                    {isEditing ? (
                      <FormattedMessage
                        {...detailMessages["newTaskShow.description"]}
                      />
                    ) : (
                      <FormattedMessage
                        {...headingMessage}
                        values={category ? { category } : undefined}
                      />
                    )}
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", lg: "md" }}
                    lineHeight="1.5"
                    color="gray.700"
                    marginTop="8px"
                  >
                    <FormattedMessage
                      {...messages["taskCreation.description.subheading"]}
                    />
                  </Text>
                  <DescriptionField
                    ref={ref}
                    {...descriptionField}
                    value={displayedDescription}
                    onChange={handleDescriptionChange}
                    placeholders={placeholders}
                    error={errors.description?.message}
                    isSubmitting={isSaving}
                    maxLength={DESCRIPTION_LIMITS.MAX_LENGTH}
                    isDictationSupported={isDictationSupported}
                    isListening={isListening}
                    onToggleDictation={toggleDictation}
                    photos={photos}
                    pendingPhotoCount={pendingUploads.length}
                    removingPhotoId={removingPhotoId as number | null}
                    allowedPhotoExtensions={[
                      ...DESCRIPTION_PHOTO_LIMITS.ALLOWED_EXTENSIONS,
                    ]}
                    canAddPhotos={availablePhotoSlots > 0}
                    onPhotosSelected={handlePhotosSelected}
                    onAddPhotosBlocked={notifyLimitReached}
                    onRemovePhoto={removePhoto}
                    isEditing={isEditing}
                  />
                </VStack>
                <Flex gap="8px" marginTop="32px" justifyContent="flex-end">
                  {isEditing && (
                    <Button
                      onClick={handleBack}
                      isDisabled={isSaving}
                      background="gray.300"
                      color="gray.800"
                      height="48px"
                      borderRadius="10px"
                      paddingX="22px"
                      fontSize="md"
                    >
                      <FormattedMessage
                        {...messages["taskCreation.cta.back"]}
                      />
                    </Button>
                  )}
                  <Button
                    type="submit"
                    isLoading={isSaving}
                    // Held while a photo is still uploading or being deleted, so
                    // the draft is never created (or saved) against a
                    // half-written list. And, when editing, until the config
                    // names the screen this submit navigates to.
                    isDisabled={
                      isSaving ||
                      isPhotoBusy ||
                      (isEditing && !firstEditScreenKey)
                    }
                    colorScheme="seaGreen"
                    height="48px"
                    borderRadius="10px"
                    paddingX="22px"
                    fontSize="md"
                    flex={{ base: "1", lg: "0 0 260px" }}
                    rightIcon={
                      <Icon
                        as={ArrowRight}
                        width="16px"
                        height="16px"
                        color="white"
                        display={{ base: "none", lg: "block" }}
                      />
                    }
                  >
                    <FormattedMessage
                      {...messages[
                        isEditing
                          ? "taskCreation.cta.saveAndContinue"
                          : "taskCreation.cta.getOffers"
                      ]}
                    />
                  </Button>
                </Flex>
              </form>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </>
  );
};
